import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, UserCheck, Key, Lock, Mail, Sparkles, CheckCircle2 } from "lucide-react";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { apiError } from "../services/api";
import sideImg from "../assets/brand/product1.jpg";

export default function Login() {
  const { login } = useAuth();
  const { t, lang, setLang, isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("admin"); // Default to admin tab for easy review

  const fillAdminCredentials = () => {
    setEmail("admin@thequeen.com");
    setPassword("QueenAdmin2026!");
    toast.info("Identifiants Administrateur renseignés avec succès");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(
        user.role === "admin"
          ? "Bienvenue sur le CRM The Queen Administrateur"
          : `Bienvenue, ${user.full_name?.split(" ")[0] || "Chère Cliente"} !`
      );
      const dest = user.role === "admin" ? "/admin" : location.state?.from || "/";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-12 bg-white text-[#111111]">
      
      {/* Left Editorial Visual Section */}
      <div className="hidden lg:flex lg:col-span-5 relative bg-[#111111] overflow-hidden flex-col justify-between p-12 text-white">
        <img
          src={sideImg}
          alt="The Queen Haute Dermatologie"
          className="absolute inset-0 w-full h-full object-cover opacity-60 filter brightness-[0.9] contrast-[1.1]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

        {/* Top Logo */}
        <div className="relative z-10">
          <Logo height={32} />
        </div>

        {/* Bottom Editorial Quote */}
        <div className="relative z-10 space-y-4">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-[11px] font-body tracking-[0.2em] uppercase text-[#F8F8F6]">
            <Sparkles size={12} className="text-[#FBBF24]" />
            Haute Précision &amp; CRM
          </span>
          <p className="font-display italic text-3xl sm:text-4xl leading-tight">
            « L'excellence dermatologique au service de chaque commande royale. »
          </p>
          <p className="text-xs font-body text-[#BBBBBB] tracking-widest uppercase">
            Système Centralisé de Gestion des Commandes — 58 Wilayas
          </p>
        </div>
      </div>

      {/* Right Login Form Section */}
      <div className="lg:col-span-7 flex flex-col justify-between p-6 sm:p-12 lg:p-16 bg-[#FAF8F5]">
        
        {/* Top Header & Language Selector */}
        <div className="flex items-center justify-between">
          <div className="lg:hidden">
            <Logo height={28} />
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-[#888888] font-body hidden sm:inline">Langue :</span>
            <div className="flex items-center rounded-full bg-white p-0.5 border border-[#EAE8E4] text-[11px] font-body shadow-sm">
              <button
                type="button"
                onClick={() => setLang("fr")}
                className={`px-2.5 py-1 rounded-full transition-all ${lang === "fr" ? "bg-black text-white font-medium" : "text-[#666666] hover:text-black"}`}
              >
                FR
              </button>
              <button
                type="button"
                onClick={() => setLang("ar")}
                className={`px-2.5 py-1 rounded-full transition-all ${lang === "ar" ? "bg-black text-white font-medium" : "text-[#666666] hover:text-black"}`}
              >
                عربي
              </button>
              <button
                type="button"
                onClick={() => setLang("en")}
                className={`px-2.5 py-1 rounded-full transition-all ${lang === "en" ? "bg-black text-white font-medium" : "text-[#666666] hover:text-black"}`}
              >
                EN
              </button>
            </div>
          </div>
        </div>

        {/* Center Main Form */}
        <div className="w-full max-w-md mx-auto my-8">
          
          <div className="text-center mb-8">
            <h1 className="font-display text-[#111111] text-3xl sm:text-4xl tracking-tight">
              {t("login_title")}
            </h1>
            <p className="text-xs sm:text-sm text-[#666666] mt-2 font-body">
              {t("login_subtitle")}
            </p>
          </div>

          {/* Toggle Tabs: Client vs Admin */}
          <div className="flex p-1 rounded-xl bg-[#EFECE6] border border-[#E2DED6] mb-6">
            <button
              type="button"
              onClick={() => { setActiveTab("admin"); fillAdminCredentials(); }}
              className={`flex-1 py-2 text-xs font-body font-medium rounded-lg flex items-center justify-center gap-1.5 transition-all ${
                activeTab === "admin"
                  ? "bg-white text-black shadow-sm font-semibold"
                  : "text-[#666666] hover:text-black"
              }`}
            >
              <ShieldCheck size={14} className="text-[#D97706]" />
              <span>Administration CRM</span>
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab("client"); setEmail(""); setPassword(""); }}
              className={`flex-1 py-2 text-xs font-body font-medium rounded-lg flex items-center justify-center gap-1.5 transition-all ${
                activeTab === "client"
                  ? "bg-white text-black shadow-sm font-semibold"
                  : "text-[#666666] hover:text-black"
              }`}
            >
              <UserCheck size={14} />
              <span>Espace Client</span>
            </button>
          </div>

          {/* Admin Credentials Showcase Box */}
          {activeTab === "admin" && (
            <div className="mb-6 p-4 rounded-2xl bg-white border border-[#EAE8E4] shadow-sm space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-mono uppercase tracking-wider text-[#D97706] font-semibold flex items-center gap-1.5">
                  <Key size={13} />
                  Identifiants CRM Officiels
                </span>
                <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-200">
                  Actif &amp; Prêt
                </span>
              </div>
              <div className="text-xs font-mono space-y-1 bg-[#FAF8F5] p-3 rounded-xl border border-[#EDE8E0]">
                <p><span className="text-[#888888]">Email :</span> <strong className="text-[#111111]">admin@thequeen.com</strong></p>
                <p><span className="text-[#888888]">Mot de passe :</span> <strong className="text-[#111111]">QueenAdmin2026!</strong></p>
              </div>
              <button
                type="button"
                onClick={fillAdminCredentials}
                className="w-full text-center text-xs text-[#111111] hover:text-[#D97706] font-body font-medium py-1 underline decoration-dotted transition-colors"
              >
                ⚡ Remplir automatiquement ces identifiants
              </button>
            </div>
          )}

          {error && (
            <div className="mb-5 text-xs rounded-xl p-3 bg-red-50 text-red-700 border border-red-200 flex items-center gap-2">
              <span>⚠</span>
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-eyebrow text-[#555555] block mb-1.5" htmlFor="email">
                {t("login_email")}
              </label>
              <div className="relative flex items-center">
                <Mail size={16} className="absolute left-3.5 text-[#999999]" />
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@thequeen.com"
                  className="w-full bg-white border border-[#EAE8E4] rounded-xl pl-10 pr-4 text-sm font-body outline-none focus:border-[#111111] transition-all"
                  style={{ height: 48 }}
                />
              </div>
            </div>

            <div>
              <label className="label-eyebrow text-[#555555] block mb-1.5" htmlFor="password">
                {t("login_password")}
              </label>
              <div className="relative flex items-center">
                <Lock size={16} className="absolute left-3.5 text-[#999999]" />
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="w-full bg-white border border-[#EAE8E4] rounded-xl pl-10 pr-4 text-sm font-body outline-none focus:border-[#111111] transition-all"
                  style={{ height: 48 }}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-queen-primary w-full h-12 uppercase tracking-wider text-xs font-semibold mt-2 shadow-lg shadow-black/10 flex items-center justify-center gap-2"
            >
              <span>{loading ? t("login_loading") : (activeTab === "admin" ? "Accéder au CRM Admin" : t("login_btn"))}</span>
              <ArrowRight size={15} className={isRTL ? "rotate-180" : ""} />
            </button>
          </form>

          {activeTab === "client" && (
            <p className="text-xs text-[#666666] mt-6 text-center font-body">
              {t("login_no_account")}{" "}
              <Link to="/register" className="text-[#111111] font-semibold underline underline-offset-4">
                {t("login_create_account")}
              </Link>
            </p>
          )}

          <div className="text-center mt-6">
            <Link to="/" className="text-xs text-[#777777] hover:text-[#111111] font-body transition-colors">
              {t("login_back_home")}
            </Link>
          </div>

        </div>

        {/* Footer info */}
        <div className="text-center text-[11px] text-[#999999] font-body">
          © {new Date().getFullYear()} The Queen — Haute Dermatologie. {t("all_rights_reserved")}
        </div>

      </div>

    </div>
  );
}
